import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { addFormulation, clearRefresh } from "../utils/formulationSlice";
import { BASE_URL } from "../utils/constants";
import Loader from "./Loader";
import FormulationTable from "./FormulationTable";
import FormulationDetail from "./FormulationDetail";

const Formulation = () => {
  const location = useLocation();
  const isDetailPage = location.pathname.includes(
    "/formulation/formulationDetail"
  );

  const formulationListData = useSelector((state) => state.formulation.list);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [showCreateNewFormulation, setShowCreateNewFormulation] =
    useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(40);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [paginationInfo, setPaginationInfo] = useState({
    totalCount: 0,
    page: 1,
    limit: 40,
    totalPages: 1,
  });
  const refreshNeeded = useSelector((state) => state.formulation.refreshNeeded);

  useEffect(() => {
    if (refreshNeeded) {
      fetchFormulationList(page, limit, debouncedSearch);
      dispatch(clearRefresh());
    }
  }, [refreshNeeded]);

  useEffect(() => {
    if (!isDetailPage) {
      setSearchTerm("");
      setDebouncedSearch("");
      setPage(1);
    }
  }, [location.pathname]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 1000);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchFormulationList(page, limit, debouncedSearch);
  }, [page, debouncedSearch]);

  const fetchFormulationList = async (page = 1, limit = 40, search = "") => {
    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/formulations`, {
        params: {
          page,
          limit,
          search,
        },
        withCredentials: true,
      });

      dispatch(addFormulation(res.data.data));
      setPaginationInfo(res.data.meta);
    } catch (err) {
      console.error("Error fetching material list:", err);
      dispatch(addFormulation([]));
      setPaginationInfo({ totalCount: 0, page: 1, limit, totalPages: 1 });
    } finally {
      setLoading(false);
    }
  };

  const goToDetailPage = () => {
    setShowCreateNewFormulation(true);
    setSelectedId(null);
  };

  const listClickHandler = (id) => {
    setSelectedId(id);
    setShowCreateNewFormulation(false);
  };

  const goBackToList = () => {
    setSelectedId(null);
    setShowCreateNewFormulation(false);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearch("");
    setPage(1);
  };

  const onDeleteFormulation = async (id) => {
    try {
      const confirmed = window.confirm("Are you sure you want to delete this material?");
      if (!confirmed) return;

      const res = await axios.delete(`${BASE_URL}/formulations/${id}`, {
        withCredentials: true,
      });

      console.log(res.data.message);
      alert("Formulation deleted successfully!");
      await fetchFormulationList();
    } catch (error) {
      console.error(error);
      alert(
        error?.response?.data?.message ||
        "Failed to delete Formulation. Please try again."
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader size={12} text="Loading Application..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {selectedId === null && !showCreateNewFormulation && (
        <div className={isDetailPage ? "hidden" : ""}>
          <div className="flex justify-end px-6 py-4">
            <button
              onClick={goToDetailPage}
              className="bg-pink-600 text-white px-4 py-2 rounded hover:bg-pink-700 transition-colors"
            >
              + Create New
            </button>
          </div>

          <div className="px-6 pt-2">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Formulation List
            </h1>

            <div className="relative mb-6 w-full max-w-md">
              <input
                type="text"
                placeholder="Search by Formulation Name or Code"
                className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 transition"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-xl"
                >
                  &times;
                </button>
              )}
            </div>

            {formulationListData.length === 0 ? (
              <div className="text-gray-500 text-center mt-10">
                No formulation found.
              </div>
            ) : (
              <FormulationTable
                formulations={formulationListData}
                pagination={paginationInfo}
                onPageChange={(p) => setPage(p)}
                listClickHandler={listClickHandler}
                onDelete={onDeleteFormulation}
              />
            )}
          </div>
        </div>
      )}
      {selectedId && (
        <FormulationDetail id={selectedId} goBackToList={goBackToList} />
      )}

      {showCreateNewFormulation && (
        <FormulationDetail goBackToList={goBackToList} />
      )}
    </div>
  );
};

export default Formulation;
